import { OrgConfigurationKey, useOrgConfiguration } from '@src/providers';
import { useTradingSettings } from '@src/providers/AppConfigProvider';
import {
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  CUSTOMER_QUOTE,
  DEFAULT_MAX_ROWS,
  EMPTY_ARRAY,
  EMPTY_OBJECT,
  ExportMenuItems,
  FormControlSizes,
  isGridApiReady,
  QuoteStatusEnum,
  removeEmptyFilters,
  Toggle,
  Tooltip,
  useAccordionFilterBuilder,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useDisclosureWithContext,
  usePersistedBlotterTable,
  useWsBlotterTable,
  WSBlotterTableMaxRecordsReachedWarning,
  type Column,
  type CustomerQuote,
  type RelatedEntitiesConfig,
  type WebsocketRequest,
} from '@talos/kyoko';
import type { RowSelectionOptions, SelectionChangedEvent } from 'ag-grid-community';
import type { RowDoubleClickedEvent } from 'ag-grid-enterprise';
import { uniq } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FixQuotePriceDialog } from '../../../../components/FixQuotePriceDialog/FixQuotePriceDialog';
import { manualRFQPricingThunks } from '../../../../components/OMS/ManualRFQPricing/ManualRFQPricingSlice';
import { useFeatureFlag } from '../../../../hooks';
import { useCurrentAppLayoutId } from '../../../../hooks/useAppLayoutId';
import { useAppStateDispatch } from '../../../../providers/AppStateProvider';
import type { ManualRFQ } from './types';
import { useManualRFQsBlotterColumns } from './useManualRFQsBlotterColumns';
import {
  onlyServerFilterKeys,
  useManualRFQsBlotterFilter,
  type ManualRFQsBlotterFilter,
} from './useManualRFQsBlotterFilter';
import { useManualRFQsBlotterMenu } from './useManualRFQsBlotterMenu';
import { getManualRFQActions } from './utils';

interface ManualRFQsTabData {
  showAllDecimals?: boolean;
}

export interface ManualRFQsBlotterProps {
  blotterID: string;
  tabLabel?: string;
  defaultColumns?: (keyof ManualRFQ | Partial<Column>)[];
  defaultFilter?: ManualRFQsBlotterFilter;
  initialRequest?: Partial<WebsocketRequest>;
  portalId?: string;

  tabData?: ManualRFQsTabData;
  onUpdateTabData?: ({ showAllDecimals }: ManualRFQsTabData) => void;

  onOpenCustomerQuoteDrawer: (quote: CustomerQuote) => void;
}

const SELECTION_MULTI_PARAMS_CHECKBOXES = {
  mode: 'multiRow',
  hideDisabledCheckboxes: true,
  checkboxes: true,
  headerCheckbox: true,
  enableClickSelection: false,
  // We treat this "getManualRFQActions" function as our source of biz logic truth, so jumping through some hoops to use that here, too.
  isRowSelectable: row => (row.data ? getManualRFQActions(row.data).some(action => action.type === 'fix') : false),
} as const satisfies RowSelectionOptions<ManualRFQ>;

export function ManualRFQsBlotter({
  blotterID,
  tabLabel,
  defaultColumns,
  defaultFilter,
  initialRequest,

  portalId,
  tabData,
  onUpdateTabData,

  onOpenCustomerQuoteDrawer,
}: ManualRFQsBlotterProps) {
  const dispatch = useAppStateDispatch();
  const { showAllDecimals } = tabData ?? EMPTY_OBJECT;
  const { getConfig } = useOrgConfiguration();
  const { showAllDecimalsInBlotters } = useTradingSettings();
  const { showBlotterPauseButton, enableApplyFixingDialogV2 } = useFeatureFlag();
  const [selectedRows, setSelectedRows] = useState<ManualRFQ[]>([]);
  const fixQuotePriceDialog = useDisclosureWithContext<ManualRFQ[]>();

  const handleRowFixClick = useCallbackRef((quote: ManualRFQ) => {
    fixQuotePriceDialog.open([quote]);
  });

  const navigate = useNavigate();
  const layoutId = useCurrentAppLayoutId();

  const handleRowQuoteClick = useCallbackRef((quote: ManualRFQ) => {
    dispatch(manualRFQPricingThunks.prime({ quote, navigate, layoutId }));
  });

  const handleShowQuoteClick = useCallbackRef((quote: ManualRFQ) => {
    onOpenCustomerQuoteDrawer(quote);
  });

  const defaultColumnDefinitions = useManualRFQsBlotterColumns({
    defaultColumns,
    onFixClick: handleRowFixClick,
    onQuoteClick: handleRowQuoteClick,
    onShowQuoteClick: handleShowQuoteClick,
  });

  const persistedBlotterTable = usePersistedBlotterTable(blotterID, {
    filter: defaultFilter,
  });

  const handleDoubleClickRow = useCallbackRef(({ data }: RowDoubleClickedEvent<ManualRFQ>) => {
    if (!data) {
      return;
    }
    onOpenCustomerQuoteDrawer(data);
  });

  const handleRowSelectionChanged = useCallback(({ api }: SelectionChangedEvent<ManualRFQ>) => {
    if (!isGridApiReady(api)) {
      return;
    }
    const rows = api.getSelectedRows();
    setSelectedRows(rows.filter(node => node.QuoteStatus === QuoteStatusEnum.PendingFix));
  }, []);

  const filterResults = useManualRFQsBlotterFilter({
    persistedBlotterTable,
  });
  const { clientSideFilter: clientLocalFilter, blotterTableFilterProps, filterBuilderProps } = filterResults;

  const filterBuilderAccordion = useAccordionFilterBuilder({
    filterBuilderProps,
  });

  const contextMenu = useManualRFQsBlotterMenu({
    openClause: filterBuilderAccordion.openClause,
    filterableProperties: filterBuilderProps.properties,
  });
  const relatedEntities = useMemo<RelatedEntitiesConfig<ManualRFQ>>(
    () => ({
      symbolSources: [
        {
          id: 'Symbol',
          title: 'Security',
          getSymbol: row => row.Symbol,
          lookup: 'security',
          optional: true,
        },
      ],
    }),
    []
  );

  const blotterTableSettings = useMemo(
    () => ({
      showAllDecimals: showAllDecimals ?? false,
    }),
    [showAllDecimals]
  );

  const gridOptions = useMemo(
    () => ({
      onRowDoubleClicked: handleDoubleClickRow,
      onSelectionChanged: handleRowSelectionChanged,
      rowSelection: SELECTION_MULTI_PARAMS_CHECKBOXES,
      processUnpinnedColumns: () => EMPTY_ARRAY,
    }),
    [handleRowSelectionChanged, handleDoubleClickRow]
  );

  const request = useMemo(
    () => ({
      tag: blotterID,
      name: CUSTOMER_QUOTE,
      sort_by: '-SubmitTime',
      ...initialRequest,
      ...removeEmptyFilters(onlyServerFilterKeys(filterResults.filter)),
    }),
    [blotterID, initialRequest, filterResults.filter]
  );

  const blotterTable = useWsBlotterTable({
    request,
    rowID: 'RFQID' satisfies keyof ManualRFQ,
    clientLocalFilter,
    persistence: persistedBlotterTable,
    columns: defaultColumnDefinitions,
    startingRowLimit: getConfig(OrgConfigurationKey.BlotterRowsMax, DEFAULT_MAX_ROWS),
    pauseParams: {
      showPauseButton: showBlotterPauseButton,
    },
    settings: blotterTableSettings,
    contextMenuOptions: {
      getCustomContextMenuItems: contextMenu.getCustomContextMenuItems,
      relatedEntities,
    },
    gridOptions,
    exportOptions: {
      name: 'Manual RFQs',
      tabLabel: tabLabel,
    },
  });

  const extrasMenuPopover = useBlotterTableExtrasMenu();

  const handleShowAllDecimalsChange = useCallback(
    (value: boolean) => {
      onUpdateTabData?.({ showAllDecimals: value });
    },
    [onUpdateTabData]
  );

  const applyFixing = useMemo((): { enabled: boolean; disabledTooltip: string | undefined } => {
    if (selectedRows.length === 0) {
      return { enabled: true, disabledTooltip: undefined };
    }

    const selectedFixingDates = uniq(selectedRows.map(row => row.fixingDate).filter(Boolean));
    if (selectedFixingDates.length > 1) {
      return {
        enabled: false,
        disabledTooltip:
          'Several fixing dates are selected. Applying fixing across multiple fixing dates is not allowed.',
      };
    }

    const enabled = selectedRows.every(row => {
      if (row.QuoteStatus !== QuoteStatusEnum.PendingFix) {
        return false;
      }

      if (!enableApplyFixingDialogV2) {
        // V1 doesn't support fixing multiple indices at once, which V2 does
        const hasDifferingFixingIndex = row.FixingDetails?.Index !== selectedRows[0].FixingDetails?.Index;
        if (hasDifferingFixingIndex) {
          return false;
        }
      }

      return true;
    });
    return { enabled, disabledTooltip: undefined };
  }, [selectedRows, enableApplyFixingDialogV2]);

  return (
    <>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTableFilterProps}
        {...blotterTable.blotterTableFiltersProps}
        portalId={portalId}
        suffix={
          <BlotterTableExtrasMenu {...extrasMenuPopover}>
            <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />
            {/* Only show this toggle if the blotter parent supports storing the state */}
            {!showAllDecimalsInBlotters && onUpdateTabData && (
              <Toggle
                size={FormControlSizes.Small}
                checked={showAllDecimals ?? false}
                onChange={handleShowAllDecimalsChange}
                label="Show All Decimals"
              />
            )}
          </BlotterTableExtrasMenu>
        }
        prefix={
          <Tooltip tooltip={applyFixing.disabledTooltip}>
            <div>
              <Button
                size={FormControlSizes.Small}
                variant={ButtonVariants.Primary}
                disabled={!applyFixing.enabled}
                onClick={() => fixQuotePriceDialog.open(selectedRows)}
                data-testid="apply-fixing-button"
              >
                Apply Fixing
              </Button>
            </div>
          </Tooltip>
        }
      />
      <WSBlotterTableMaxRecordsReachedWarning {...blotterTable.paginationLimit} getTimestamp={r => r.SubmitTime} />
      <BlotterTable {...blotterTable} />
      {contextMenu.dialogs}
      <FixQuotePriceDialog {...fixQuotePriceDialog} />
    </>
  );
}
